import { useProvider } from '@newfront-insurance/react-provision';
import { useCallback, useMemo } from 'react';

import { createNovaTransport, probePersistedDurableActiveStream } from '../lib/transport';
import { useNovaContext } from '../providers';

interface UseChatTransportOptions {
  onReconnectUnavailable?: (chatUuid: string) => void;
  onWorkflowAttached?: (attachment: { chatUuid: string; workflowRunId: string }) => void;
}

/**
 * Hook to create and memoize the Nova chat transport.
 * Uses context values and auth provider to configure the transport.
 * The transport is recreated when context values change.
 */
export function useChatTransport({ onReconnectUnavailable, onWorkflowAttached }: UseChatTransportOptions = {}) {
  const { userUuid, accountUuid, authProvider, apiEndpoint, baseUrl } = useNovaContext();
  const { getToken } = useProvider(authProvider);

  const transport = useMemo(() => {
    return createNovaTransport({
      apiEndpoint,
      baseUrl,
      getToken,
      onReconnectUnavailable,
      onWorkflowAttached,
      userUuid,
      accountUuid,
    });
  }, [accountUuid, apiEndpoint, baseUrl, getToken, onReconnectUnavailable, onWorkflowAttached, userUuid]);

  return transport;
}

export function usePersistedDurableActiveStreamProbe() {
  const { authProvider, apiEndpoint, baseUrl } = useNovaContext();
  const { getToken } = useProvider(authProvider);

  return useCallback(
    ({ chatUuid, signal, workflowRunId }: { chatUuid: string; signal?: AbortSignal; workflowRunId: string }) =>
      probePersistedDurableActiveStream({
        apiEndpoint,
        baseUrl,
        chatUuid,
        getToken,
        signal,
        workflowRunId,
      }),
    [apiEndpoint, baseUrl, getToken],
  );
}
